import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { i18prefix } from '../../helpers';
import Hint from '../../../../../../components/hint';

import { ProjectDay } from '../../types';
import { totalMinutes } from '../mappings';
import { convertToMinutes, minToHoursInput } from '../helpers';
import HoursInput from '../../../TimeTracking/TimeTrackingEntries/components/ProjectDayTimeEntriesForm/HoursInput';
import classNames from 'classnames';
import {
  useRestrictFutureEntries,
  useTimeTrackingRestriction,
} from '../../../../hooks/timeTrackingSettings';
import { toISODateOnly } from '../../../../../../utils/dateUtils';

type Props = {
  days: ProjectDay[];
  disabled: boolean;
  onChange: (days: ProjectDay[]) => void;
};

type HourInputProps = {
  day: ProjectDay;
  disabled: boolean;
  onChange: (dateId: string, hours: number) => void;
};

const HourInput: React.FC<HourInputProps> = ({ day, disabled, onChange }) => {
  if (day.timesTracked.length > 1 || day.status !== 'ACTIVE') {
    return <ReadOnlyHourInput day={day} />;
  }

  const hours = minToHoursInput(totalMinutes([day]));

  return (
    <StyledDiv>
      <HoursInput
        hours={hours}
        disabled={disabled}
        compactMode
        onChange={(newHours) => onChange(day.dateId, newHours ?? 0)}
      />
    </StyledDiv>
  );
};

const ReadOnlyHourInput: React.FC<Pick<HourInputProps, 'day'>> = ({ day }) => {
  const { t } = useTranslation();

  const hours = minToHoursInput(totalMinutes([day]));

  return (
    <StyledDiv>
      <Hint
        id={`project-day-hint-${day.dateId}`}
        mode="tooltip"
        text={t(getReadOnlyHintMessageKey(day))}>
        <HoursInput
          hours={hours}
          disabled={true}
          compactMode
          onChange={() => undefined}
        />
      </Hint>
    </StyledDiv>
  );
};

const ProjectWeekTimeTracking: React.FC<Props> = ({
  days,
  disabled,
  onChange,
}) => {
  const { restrictFutureEntriesForADay } = useRestrictFutureEntries();
  const { getProjectTrackingRestrictedReason } = useTimeTrackingRestriction();

  const handleInputChange = useCallback(
    (dateId: string, hours: number) => {
      onChange(
        days.map((day) => {
          if (day.timesTracked.length > 1 || day.dateId !== dateId) {
            return day;
          }

          return {
            ...day,
            timesTracked: [
              {
                ...day.timesTracked[0],
                minutes: convertToMinutes(hours),
              },
            ],
          };
        }),
      );
    },
    [onChange, days],
  );

  return (
    <>
      {days.map((day) => {
        const restrictionReason = getProjectTrackingRestrictedReason(
          toISODateOnly(day.date),
        );
        return (
          <td
            className={classNames({
              'cell-bl': true,
              highlighted: !!restrictionReason,
            })}
            key={day.dateId}>
            <HourInput
              day={day}
              disabled={
                disabled ||
                restrictFutureEntriesForADay(day.date) ||
                !!restrictionReason
              }
              onChange={handleInputChange}
            />
          </td>
        );
      })}
    </>
  );
};

export default ProjectWeekTimeTracking;

const StyledDiv = styled.div({
  minWidth: '7.5rem',
});

function getReadOnlyHintMessageKey(day: ProjectDay) {
  if (day.status === 'FINISHED') {
    return `${i18prefix}.projectFinishedMessage`;
  } else if (day.status === 'NOT_STARTED') {
    return `${i18prefix}.projectNotStartedMessage`;
  } else if (day.timesTracked.length > 1) {
    return `${i18prefix}.disabledByProjectsMessage`;
  } else {
    throw new Error('Invalid case: getReadOnlyHintMessageKey');
  }
}
